import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';

/**
 * Filter state with common pagination fields
 */
export interface BaseFilters {
  page: number;
  limit: number;
  search?: string;
  [key: string]: any;
}

/**
 * Hook return type
 */
export interface UseAdminFiltersReturn<T extends BaseFilters> {
  filters: T;
  debouncedFilters: T;
  setFilter: (key: keyof T, value: any) => void;
  setFilters: (filters: Partial<T>) => void;
  clearFilters: () => void;
  isFiltering: boolean;
}

/**
 * Admin filters hook - manages filter state with URL synchronization
 *
 * Features:
 * - Parses URL query params into filter state
 * - Syncs filter changes back to URL (shareable filtered views)
 * - Debounces search inputs to prevent excessive API calls
 * - Provides convenient setFilter/clearFilters functions
 * - Detects when filters are active (isFiltering flag)
 *
 * @param defaultFilters - Default filter values
 * @param searchDebounceMs - Debounce delay for search field (default: 500ms)
 * @returns Filter state and control functions
 *
 * @example
 * const { filters, debouncedFilters, setFilter, clearFilters } = useAdminFilters({
 *   page: 1,
 *   limit: 25,
 *   search: '',
 *   category: '',
 *   published: undefined,
 * });
 *
 * // Use debouncedFilters for API calls to avoid rapid requests
 * useEffect(() => {
 *   fetchProducts(debouncedFilters);
 * }, [debouncedFilters]);
 */
export function useAdminFilters<T extends BaseFilters>(
  defaultFilters: T,
  searchDebounceMs: number = 500
): UseAdminFiltersReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params into filter state
  const initialFilters = useMemo((): T => {
    const parsed = { ...defaultFilters };

    for (const key in defaultFilters) {
      const paramValue = searchParams.get(key);

      if (paramValue !== null) {
        // Parse based on default value type
        const defaultValue = defaultFilters[key];

        if (typeof defaultValue === 'number') {
          parsed[key] = parseFloat(paramValue) as any;
        } else if (typeof defaultValue === 'boolean') {
          parsed[key] = (paramValue === 'true') as any;
        } else {
          parsed[key] = paramValue as any;
        }
      }
    }

    return parsed;
  }, []); // Only parse once on mount

  const [filters, setFiltersState] = useState<T>(initialFilters);
  const isInitialMount = useRef(true);
  const urlSyncTimeoutRef = useRef<number | null>(null);

  // Debounce the entire filter object (mainly for search field)
  const debouncedFilters = useDebounce(filters, searchDebounceMs);

  // Sync filters to URL with debouncing to avoid rapid History API calls
  useEffect(() => {
    // Skip URL sync on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear any pending URL sync
    if (urlSyncTimeoutRef.current) {
      clearTimeout(urlSyncTimeoutRef.current);
    }

    // Debounce URL updates to prevent "Too many calls to History API" error
    urlSyncTimeoutRef.current = setTimeout(() => {
      const params = new URLSearchParams();

      for (const key in filters) {
        const value = filters[key];

        // Only add non-default values to URL
        if (value !== undefined && value !== null && value !== '' && value !== defaultFilters[key]) {
          params.set(key, String(value));
        }
      }

      setSearchParams(params, { replace: true });
    }, 100); // 100ms debounce for URL sync

    // Cleanup timeout on unmount
    return () => {
      if (urlSyncTimeoutRef.current) {
        clearTimeout(urlSyncTimeoutRef.current);
      }
    };
  }, [filters, defaultFilters, setSearchParams]);

  // Set a single filter
  const setFilter = useCallback((key: keyof T, value: any) => {
    setFiltersState((prev) => ({
      ...prev,
      [key]: value,
      // Reset to page 1 when filters change (except page itself)
      ...(key !== 'page' && key !== 'limit' ? { page: 1 } : {}),
    }));
  }, []);

  // Set multiple filters at once
  const setFilters = useCallback((newFilters: Partial<T>) => {
    setFiltersState((prev) => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 when filters change (unless page is being set)
      ...(!newFilters.page ? { page: 1 } : {}),
    }));
  }, []);

  // Clear all filters back to defaults
  const clearFilters = useCallback(() => {
    setFiltersState(defaultFilters);
  }, [defaultFilters]);

  // Check if any filters are active (excluding page/limit)
  const isFiltering = useMemo(() => {
    for (const key in filters) {
      if (key === 'page' || key === 'limit') continue;

      const value = filters[key];
      const defaultValue = defaultFilters[key];

      if (value !== defaultValue && value !== '' && value !== undefined && value !== null) {
        return true;
      }
    }
    return false;
  }, [filters, defaultFilters]);

  return {
    filters,
    debouncedFilters,
    setFilter,
    setFilters,
    clearFilters,
    isFiltering,
  };
}
